import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import MuxPlayer from "@mux/mux-player-react";
import { ArrowLeft, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { countryFlag, countryName, typeLabel } from "@/lib/constants";

export default function Watch() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const lastSent = useRef(0);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    api.get(`/content/${id}`).then((r) => setItem(r.data)).catch(() => setItem(false));
  }, [id]);

  const sendProgress = (position, duration) => {
    if (!user) return;
    const now = Date.now();
    if (now - lastSent.current < 8000 && position !== duration) return;
    lastSent.current = now;
    const watchTime = Math.round((now - startedAt.current) / 1000);
    api.post(`/content/${id}/watch`, {
      position_seconds: position || 0, duration_seconds: duration || 0,
      watch_time_seconds: watchTime,
    }).catch(() => {});
  };

  if (item === false) return <Screen><p className="text-white/50">Content not found.</p></Screen>;
  if (!item) return <Screen><div className="w-10 h-10 border-4 border-[#E50914] border-t-transparent rounded-full animate-spin" /></Screen>;

  if (!item.can_watch) {
    return (
      <Screen>
        <div className="text-center max-w-md px-6">
          <Lock className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
          <h2 className="font-display font-black text-3xl mb-2">Premium Content</h2>
          <p className="text-white/60 mb-6">Subscribe to Tazama Africa to stream “{item.title}” and thousands of African titles.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/subscriptions" className="px-6 py-3 rounded-full bg-[#E50914] hover:bg-[#FF2A3B] font-semibold">View Plans</Link>
            <Link to={`/movie/${id}`} className="px-6 py-3 rounded-full bg-white/10 border border-white/20">Back</Link>
          </div>
        </div>
      </Screen>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <div className="flex items-center gap-3 px-4 sm:px-8 py-4">
        <button onClick={() => navigate(-1)} data-testid="watch-back" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="font-display font-bold text-lg leading-tight">{item.title}</h1>
          <p className="text-xs text-white/50">{countryFlag(item.country)} {countryName(item.country)} · {item.language} · {typeLabel(item.content_type)}</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-2 sm:px-8 pb-8" data-testid="watch-player">
        <div className="w-full max-w-6xl aspect-video rounded-xl overflow-hidden bg-black border border-white/10">
          {item.playback_id ? (
            <MuxPlayer
              playbackId={item.playback_id}
              streamType="on-demand"
              accentColor="#E50914"
              metadata={{ video_id: item.id, video_title: item.title, viewer_user_id: user?.id }}
              className="w-full h-full"
              onTimeUpdate={(e) => sendProgress(e.target.currentTime, e.target.duration)}
              onEnded={(e) => sendProgress(e.target.duration, e.target.duration)}
            />
          ) : item.video_url ? (
            <video src={item.video_url} controls autoPlay className="w-full h-full" data-testid="watch-video"
              poster={item.thumbnail}
              onTimeUpdate={(e) => sendProgress(e.target.currentTime, e.target.duration)}
              onEnded={(e) => sendProgress(e.target.duration, e.target.duration)}>
              {item.subtitles?.map((s) => <track key={s} kind="subtitles" label={s} />)}
            </video>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/50">Video is still processing.</div>
          )}
        </div>
      </div>

      <div className="px-4 sm:px-8 pb-10 max-w-6xl mx-auto w-full">
        <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
      </div>
    </div>
  );
}

const Screen = ({ children }) => (
  <div className="min-h-screen bg-black flex items-center justify-center">{children}</div>
);
